/* AK design layer — behaviour. No dependencies; relies on Dawn's cart-drawer / pubsub when present. */
(() => {
  const cfg = Object.assign(
    { code: '', threshold: 2, amount: '', exitIntent: true, returnToast: true, tabTitle: true, cartUrl: '/cart', strings: {} },
    window.akConfig || {}
  );
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const store = {
    get(k, d = null) { try { const v = (k.startsWith('s:') ? sessionStorage : localStorage).getItem(k); return v === null ? d : JSON.parse(v); } catch (e) { return d; } },
    set(k, v) { try { (k.startsWith('s:') ? sessionStorage : localStorage).setItem(k, JSON.stringify(v)); } catch (e) {} },
  };
  const yen = (cents) => '¥' + Math.round(cents / 100).toLocaleString('ja-JP');
  // Reader number style: a lone digit is full-width (乙６), two or more digits stay half-width
  const ja = (v) => String(v).replace(/(^|[^0-9０-９.,:\/#\-A-Za-z_])([0-9])(?![0-9０-９.,:%A-Za-z_\/])/g, (m, a, d) => a + String.fromCharCode(0xff10 + Number(d)));

  /* ---------------- Text splitting ---------------- */
  function splitChars(root) {
    if (root.dataset.akSplitDone) return [];
    root.dataset.akSplitDone = '1';
    const out = [];
    const walk = (node) => {
      [...node.childNodes].forEach((n) => {
        if (n.nodeType === 3) {
          const frag = document.createDocumentFragment();
          for (const ch of n.textContent) {
            if (/\s/.test(ch)) { frag.appendChild(document.createTextNode(ch)); continue; }
            const s = document.createElement('span');
            s.className = 'ak-ch';
            s.textContent = ch;
            s.setAttribute('aria-hidden', 'true');
            frag.appendChild(s);
            out.push(s);
          }
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && n.tagName !== 'BR') walk(n);
      });
    };
    if (!root.getAttribute('aria-label')) root.setAttribute('aria-label', root.textContent.replace(/\s+/g, ' ').trim());
    walk(root);
    out.forEach((s, i) => s.style.setProperty('--i', i));
    return out;
  }

  /* ---------------- Japanese phrase-aware wrapping ----------------
     Groups text into phrases (word + trailing particles/punctuation) and makes each phrase
     unbreakable, so headings never wrap as 「迎えま/すか」 or leave 「。」 alone on a line. */
  const PHRASE_SEL = '.card__heading a, .footer-block__details-content p, .ak-pitch__h, .ak-pitch__sub, .ak-pitch__checks li, .ak-pitch__contrast dt, .ak-pitch__quote p, .ak-vs li, .ak-step .ak-h3, .ak-qa summary, .ak-meter__msg, .ak-h2, .ak-h3, .ak-display, .ak-vs__title, .ak-quote, .ak-step__big, .ak-signup__big, .ak-manifesto__text p, .ak-tab b, .ak-lead, .ak-body-text, .ak-card__title a, .product__title h1, .collection-hero__title';
  const BODY_SEL = '.ak-lead, .ak-body-text, .ak-vs li, .ak-pitch__checks li, .ak-qa summary, .footer-block__details-content p';
  const segmenter = 'Segmenter' in Intl ? new Intl.Segmenter('ja', { granularity: 'word' }) : null;
  const ATTACH = /^([\u3041-\u309F]{1,3}|[、。，．・：；！？」』）】〕…ー\-,.!?:;)\]]+)$/;
  const OPENER = /[「『（【〔(\[]$/;
  const HONORIFIC = /^[ごお御]$/; // ご購入, お届け: the prefix belongs to the word after it
  // Segmenter splits compounds (練習|試合, 受験|料); keep short kanji runs and these set phrases whole
  const KANJI_TAIL = /[一-鿿々]+$/;
  const KANJI_HEAD = /^[一-鿿々]+/;
  const TERMS = ['過去問テスト', '合格ライン', '持ち帰', 'マークシート', '解答カード'];
  const PUNCT = /^[、。，．・：；！？」』）】〕…ー\-,.!?:;)\]]+$/;
  // Particles join the word before them, but a phrase that is already long stops growing; 類・問・点 stay with their number
  const attach = (prev, seg, cap) => PUNCT.test(seg) || (ATTACH.test(seg) && prev.length < cap) || (/[0-9０-９]$/.test(prev) && seg.length <= 2 && /^[一-鿿％%]/.test(seg));
  const compound = (prev, seg) => {
    if ((prev + seg).length > 10) return false;
    const t = prev.match(KANJI_TAIL), h = seg.match(KANJI_HEAD);
    if (t && h && t[0].length + h[0].length <= 5) return true;
    const c = prev + seg;
    return TERMS.some((term) => { const i = c.indexOf(term, Math.max(0, prev.length - term.length + 1)); return i >= 0 && i < prev.length && i + term.length > prev.length; });
  };
  function phraseWrap(root) {
    if (!segmenter || root.dataset.akPhrase) return;
    root.dataset.akPhrase = '1';
    // Running copy uses shorter phrases so lines fill evenly; headings keep longer ones whole
    const cap = root.matches(BODY_SEL) ? 9 : 14;
    const walk = (node) => {
      [...node.childNodes].forEach((n) => {
        if (n.nodeType === 1) { if (n.tagName !== 'BR') walk(n); return; }
        if (n.nodeType !== 3 || !n.textContent.trim()) return;
        const phrases = [];
        for (const { segment } of segmenter.segment(n.textContent)) {
          const prev = phrases[phrases.length - 1];
          if (HONORIFIC.test(segment)) phrases.push(segment);
          else if (prev !== undefined && !/\s$/.test(prev) && (attach(prev, segment, cap) || OPENER.test(prev) || HONORIFIC.test(prev) || compound(prev, segment))) phrases[phrases.length - 1] = prev + segment;
          else phrases.push(segment);
        }
        const frag = document.createDocumentFragment();
        phrases.forEach((ph) => {
          if (/^\s+$/.test(ph)) { frag.appendChild(document.createTextNode(ph)); return; }
          const w = document.createElement('span');
          w.className = 'ak-w';
          w.textContent = ph;
          frag.appendChild(w);
        });
        n.replaceWith(frag);
      });
    };
    walk(root);
  }
  function initPhrases(scope = document) { $$(PHRASE_SEL, scope).forEach(phraseWrap); }
  /* Number style for counts printed by Dawn-owned markup (e.g. the offer line in main-product) */
  function initNums(scope = document) {
    $$('.ak-meter__msg', scope).forEach((el) => {
      const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      for (let n = w.nextNode(); n; n = w.nextNode()) n.textContent = ja(n.textContent);
    });
  }

  /* ---------------- Product description: tidy spacer paragraphs, fold long text ---------------- */
  function initDescription() {
    const el = $('.product__description');
    if (!el || el.dataset.akDesc) return;
    el.dataset.akDesc = '1';
    $$('p', el).forEach((para) => { if (!para.textContent.trim() && !para.querySelector('img, iframe, video')) para.classList.add('ak-gap'); });
    if (el.scrollHeight < 560) return;
    el.classList.add('ak-desc', 'is-folded');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ak-desc__more';
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = '<span>商品説明をすべて読む</span><i aria-hidden="true">+</i>';
    btn.addEventListener('click', () => {
      const open = el.classList.toggle('is-folded') === false;
      btn.setAttribute('aria-expanded', open);
      btn.firstElementChild.textContent = open ? '商品説明を閉じる' : '商品説明をすべて読む';
      btn.lastElementChild.textContent = open ? '−' : '+';
      if (!open) el.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    });
    el.after(btn);
  }

  /* ---------------- Reveal on scroll ---------------- */
  function initReveal(scope = document) {
    $$('[data-ak-split]', scope).forEach(splitChars);
    const els = $$('[data-ak-reveal], [data-ak-split], [data-ak-line]', scope).filter((el) => !el.classList.contains('is-in'));
    if (reduce || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------------- Manifesto: chars light up with scroll ---------------- */
  function initManifesto(scope = document) {
    $$('[data-ak-scrolllit]', scope).forEach((el) => {
      const chars = splitChars(el);
      if (reduce) { chars.forEach((c) => c.classList.add('is-lit')); return; }
      let lit = -1, ticking = false;
      const update = () => {
        ticking = false;
        const r = el.getBoundingClientRect(), vh = window.innerHeight;
        const p = Math.min(1, Math.max(0, (vh * 0.85 - r.top) / (r.height + vh * 0.15)));
        const n = Math.round(p * chars.length);
        if (n === lit) return;
        for (let i = Math.min(n, lit); i < Math.max(n, lit); i++) if (chars[i]) chars[i].classList.toggle('is-lit', i < n);
        lit = n;
      };
      chars.forEach((c, i) => c.classList.toggle('is-lit', false));
      lit = 0;
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      update();
    });
  }

  /* ---------------- Mark sheet hero ---------------- */
  function initSheet(scope = document) {
    $$('[data-ak-sheet]', scope).forEach((sheet) => {
      if (sheet.dataset.ready) return;
      sheet.dataset.ready = '1';
      const rows = $$('.ak-sheet__row', sheet);
      const out = $('[data-ak-score]', sheet);
      const line = $('.ak-sheet__line', sheet);
      const total = rows.length;
      // Deterministic "answer key" and an attempt that lands just above the pass line.
      const key = rows.map((_, i) => (i * 7 + 3) % 4);
      const wrongAt = new Set(rows.map((_, i) => i).filter((i) => i % 4 === 2).slice(0, Math.floor(total * 0.25)));
      const render = () => {
        let marked = 0, right = 0;
        rows.forEach((row, i) => {
          const on = $('.ak-bubble.is-on', row);
          if (on) { marked++; if (+on.dataset.v === key[i]) right++; }
        });
        const pct = Math.round((right / total) * 100);
        if (out) out.innerHTML = `${String(right).padStart(2, '0')}<span>/${total}</span>`;
        if (line) { line.style.setProperty('--score', pct); line.classList.toggle('is-pass', pct >= 60); }
        return marked;
      };
      const mark = (row, v) => {
        $$('.ak-bubble', row).forEach((b) => { b.classList.toggle('is-on', +b.dataset.v === v); b.setAttribute('aria-pressed', +b.dataset.v === v); });
      };
      sheet.addEventListener('click', (e) => {
        const b = e.target.closest('.ak-bubble');
        if (!b) return;
        sheet.dataset.user = '1';
        mark(b.closest('.ak-sheet__row'), +b.dataset.v);
        render();
      });
      const autoplay = () => {
        if (reduce) {
          rows.forEach((row, i) => mark(row, wrongAt.has(i) ? (key[i] + 1) % 4 : key[i]));
          render();
          return;
        }
        let i = 0;
        const step = () => {
          if (sheet.dataset.user || i >= total) return;
          mark(rows[i], wrongAt.has(i) ? (key[i] + 1) % 4 : key[i]);
          render();
          i++;
          setTimeout(step, 90 + Math.random() * 90);
        };
        step();
      };
      const io = new IntersectionObserver((en) => { if (en[0].isIntersecting) { io.disconnect(); setTimeout(autoplay, 500); } }, { threshold: 0.3 });
      io.observe(sheet);
      render();

      // Subtle 3D tilt on fine pointers.
      const card = $('.ak-sheet__card', sheet);
      if (card && !reduce && window.matchMedia('(pointer: fine)').matches) {
        sheet.addEventListener('pointermove', (e) => {
          const r = sheet.getBoundingClientRect();
          card.style.setProperty('--rx', (((e.clientX - r.left) / r.width - 0.5) * 8).toFixed(2));
          card.style.setProperty('--ry', (-((e.clientY - r.top) / r.height - 0.5) * 8).toFixed(2));
        });
        sheet.addEventListener('pointerleave', () => { card.style.setProperty('--rx', 0); card.style.setProperty('--ry', 0); });
      }
    });
  }

  /* ---------------- Hero stack: pointer parallax ---------------- */
  function initStack(scope = document) {
    if (reduce || !window.matchMedia('(pointer: fine)').matches) return;
    $$('[data-ak-stack]', scope).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      const hero = el.closest('.ak-hero') || el;
      hero.addEventListener('pointermove', (e) => {
        const r = hero.getBoundingClientRect();
        el.style.setProperty('--mx', ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
        el.style.setProperty('--my', ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
      });
      hero.addEventListener('pointerleave', () => { el.style.setProperty('--mx', 0); el.style.setProperty('--my', 0); });
    });
  }

  /* ---------------- Cart helpers ---------------- */
  async function getCart() {
    const r = await fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    return r.json();
  }
  async function addToCart(variantId, btn) {
    const drawer = document.querySelector('cart-drawer');
    const body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (drawer && drawer.getSectionsToRender) {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    btn && btn.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || 'error');
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ak-finder', productVariantId: variantId, cartData: data });
      }
      if (drawer && data.sections) {
        drawer.classList.remove('is-empty');
        drawer.renderContents({ ...data, id: data.items?.[0]?.id ?? variantId });
      } else {
        window.location = window.routes?.cart_url || '/cart';
      }
      if (btn) { const t = btn.querySelector('[data-label]'); if (t) { const o = t.textContent; t.textContent = cfg.strings.added || '追加しました'; setTimeout(() => (t.textContent = o), 1800); } }
    } catch (e) {
      alert(e.message || 'カートに追加できませんでした。');
    } finally {
      btn && btn.removeAttribute('aria-busy');
    }
  }
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-ak-add]');
    if (!b) return;
    e.preventDefault();
    addToCart(b.dataset.akAdd, b);
  });

  /* Auto-apply the multi-item discount at checkout when the cart qualifies. */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"]');
    if (!btn || !cfg.code) return;
    e.preventDefault();
    let target = '/checkout';
    try {
      const cart = await getCart();
      if (cart.item_count >= cfg.threshold) target = `/checkout?discount=${encodeURIComponent(cfg.code)}`;
    } catch (err) {}
    window.location.href = target;
  }, true);

  /* ---------------- Finder ---------------- */
  function initFinder(scope = document) {
    $$('[data-ak-finder]', scope).forEach((root) => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      const tabs = $$('[role="tab"]', root);
      const panels = $$('[role="tabpanel"]', root);
      const filtersEl = $('[data-ak-filters]', root);
      const countEl = $('[data-ak-count]', root);
      const DIMS = [
        { key: 'rui', label: '類', all: 'すべて' },
        { key: 'grade', label: '区分', all: 'すべて' },
        { key: 'format', label: '形式', all: 'すべて' },
      ];
      let state = {};
      const LIMIT = window.matchMedia('(max-width: 749px)').matches ? 6 : 8;
      root.addEventListener('click', (e) => {
        if (!e.target.closest('[data-ak-more]')) return;
        const panel = activePanel();
        panel.dataset.expanded = '1';
        apply();
        initReveal(panel);
      });

      const activePanel = () => panels.find((p) => !p.hidden);
      const apply = () => {
        const panel = activePanel();
        if (!panel) return;
        let n = 0;
        const limit = panel.dataset.expanded ? Infinity : LIMIT;
        $$('.ak-card', panel).forEach((c) => {
          const ok = DIMS.every((d) => !state[d.key] || c.dataset[d.key] === state[d.key]);
          if (ok) n++;
          c.hidden = !ok || n > limit;
        });
        const more = $('.ak-finder__more', panel);
        if (more) { more.hidden = n <= limit; const mn = $('[data-ak-more-n]', more); if (mn) mn.textContent = `(${ja(n)})`; }
        const empty = $('.ak-empty', panel);
        if (empty) empty.hidden = n > 0;
        if (countEl) countEl.innerHTML = `<b>${ja(n)}</b> 件の教材`;
      };
      const buildFilters = () => {
        const panel = activePanel();
        if (!filtersEl || !panel) return;
        filtersEl.innerHTML = '';
        state = {};
        const cards = $$('.ak-card', panel);
        DIMS.forEach((d) => {
          const vals = [...new Set(cards.map((c) => c.dataset[d.key]).filter(Boolean))];
          if (vals.length < 2) return;
          const order = (v) => { const m = v.match(/\d+/); return v.startsWith('特') ? -1 : m ? +m[0] : 99; };
          if (d.key === 'rui') vals.sort((a, b) => order(a) - order(b));
          const row = document.createElement('div');
          row.className = 'ak-filter';
          row.setAttribute('role', 'group');
          row.setAttribute('aria-label', d.label);
          row.innerHTML = `<span class="ak-filter__label">${d.label}</span>` +
            [`<button type="button" class="ak-chip" aria-pressed="true" data-k="${d.key}" data-v="">${d.all}</button>`]
              .concat(vals.map((v) => `<button type="button" class="ak-chip" aria-pressed="false" data-k="${d.key}" data-v="${v}">${ja(v)}</button>`))
              .join('');
          filtersEl.appendChild(row);
        });
        apply();
      };
      filtersEl && filtersEl.addEventListener('click', (e) => {
        const chip = e.target.closest('.ak-chip');
        if (!chip) return;
        state[chip.dataset.k] = chip.dataset.v;
        $$(`.ak-chip[data-k="${chip.dataset.k}"]`, filtersEl).forEach((c) => c.setAttribute('aria-pressed', c === chip));
        apply();
      });
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on);
          t.tabIndex = on ? 0 : -1;
          const p = document.getElementById(t.getAttribute('aria-controls'));
          if (p) p.hidden = !on;
        });
        if (focus) tab.focus();
        buildFilters();
        initReveal(activePanel());
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => select(t));
        t.addEventListener('keydown', (e) => {
          const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (d) { e.preventDefault(); select(tabs[(i + d + tabs.length) % tabs.length], true); }
        });
      });
      // Deep link: #finder-<handle> selects that exam.
      const hashTab = tabs.find((t) => location.hash && location.hash.slice(1) === t.dataset.anchor);
      select(hashTab || tabs[0]);
    });
  }

  /* ---------------- Copy-to-clipboard ---------------- */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-ak-copy]');
    if (!b) return;
    try { await navigator.clipboard.writeText(b.dataset.akCopy); } catch (err) {}
    const o = b.textContent;
    b.textContent = 'コピーしました';
    b.classList.add('is-done');
    setTimeout(() => { b.textContent = o; b.classList.remove('is-done'); }, 1800);
  });

  /* ---------------- Newsletter: compose customer tags ---------------- */
  document.addEventListener('submit', (e) => {
    const f = e.target.closest('form.ak-signup-form');
    if (!f) return;
    const sel = $('[data-ak-interest]', f);
    const tags = $('input[name="contact[tags]"]', f);
    if (tags) tags.value = ['newsletter', 'ak-signup', sel && sel.value].filter(Boolean).join(',');
    store.set('ak:subscribed', Date.now());
  });

  /* ---------------- Sticky add-to-cart (product) ---------------- */
  function initSticky() {
    const bar = $('[data-ak-sticky]');
    const main = $('product-form .product-form__submit');
    if (!bar || !main) return;
    const io = new IntersectionObserver(([e]) => bar.classList.toggle('is-on', !e.isIntersecting && e.boundingClientRect.top < 0), { threshold: 0 });
    io.observe(main);
    $('[data-ak-sticky-add]', bar)?.addEventListener('click', () => {
      if (main.disabled || main.getAttribute('aria-disabled') === 'true') { main.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
      main.click();
    });
  }

  /* ---------------- Cart recovery: return toast, exit intent, tab title ---------------- */
  function initRecovery() {
    const path = location.pathname;
    if (/^\/(cart|checkout|account|challenge|password)/.test(path)) return;
    const toast = $('[data-ak-toast]');
    const modal = $('[data-ak-exit]');

    getCart().then((cart) => {
      const n = cart.item_count || 0;
      store.set('ak:cartCount', n);

      // 1) Returning visitor with items in cart: one gentle reminder per session.
      if (cfg.returnToast && toast && n > 0 && !store.get('s:ak:toast')) {
        const lastSeen = store.get('ak:lastSeen', 0);
        if (lastSeen && Date.now() - lastSeen > 30 * 60 * 1000) {
          $('[data-ak-toast-count]', toast).textContent = ja(n);
          setTimeout(() => toast.classList.add('is-on'), 1800);
          store.set('s:ak:toast', 1);
        }
      }
      store.set('ak:lastSeen', Date.now());

      // 2) Tab title nudge while the tab is in the background.
      if (cfg.tabTitle && n > 0) {
        const orig = document.title;
        document.addEventListener('visibilitychange', () => {
          document.title = document.hidden ? (cfg.strings.tabTitle || '教材がカートに残っています') : orig;
        });
      }

      // 3) Exit intent (fine pointers only), at most once per 3 days.
      if (!cfg.exitIntent || !modal || !window.matchMedia('(pointer: fine)').matches) return;
      const last = store.get('ak:exitShown', 0);
      if (Date.now() - last < 3 * 864e5) return;
      const hasCart = n > 0;
      if (!hasCart && store.get('ak:subscribed')) return;
      $('[data-ak-exit-cart]', modal).hidden = !hasCart;
      $('[data-ak-exit-signup]', modal).hidden = hasCart;
      if (hasCart) {
        const list = $('[data-ak-exit-items]', modal);
        if (list) list.innerHTML = cart.items.slice(0, 3).map((it) =>
          `<li>${it.image ? `<img src="${it.image}${it.image.includes('?') ? '&' : '?'}width=120" alt="" width="56" height="56" loading="lazy">` : '<span></span>'}<div>${it.product_title.replace(/【[^】]*】/g, '')}</div><span>${yen(it.final_line_price)}</span></li>`).join('');
        const need = Math.max(0, cfg.threshold - n);
        const hint = $('[data-ak-exit-hint]', modal);
        if (hint) hint.textContent = need > 0 ? `あと${ja(need)}点で${cfg.amount}OFF（購入画面で自動適用）` : `${cfg.amount}OFFが購入画面で自動適用されます`;
      }
      const armAfter = Date.now() + 8000;
      const onLeave = (e) => {
        if (e.clientY > 0 || e.relatedTarget || Date.now() < armAfter) return;
        document.removeEventListener('mouseout', onLeave);
        openModal(modal);
        store.set('ak:exitShown', Date.now());
      };
      document.addEventListener('mouseout', onLeave);
    }).catch(() => {});

    toast && toast.addEventListener('click', (e) => { if (e.target.closest('[data-ak-close]')) toast.classList.remove('is-on'); });
  }
  function openModal(m) {
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    const prev = document.activeElement;
    const focusable = () => $$('a[href], button:not([disabled]), input, select', m).filter((el) => el.offsetParent !== null);
    setTimeout(() => focusable()[0]?.focus(), 50);
    const close = () => { m.classList.remove('is-open'); m.setAttribute('aria-hidden', 'true'); document.removeEventListener('keydown', key); prev && prev.focus && prev.focus(); };
    const key = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') { const f = focusable(); if (!f.length) return; const first = f[0], last = f[f.length - 1]; if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); } else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); } }
    };
    document.addEventListener('keydown', key);
    m.onclick = (e) => { if (e.target.closest('[data-ak-close]')) close(); };
  }

  /* ---------------- Boot ---------------- */
  const boot = (scope = document) => { initNums(scope); initPhrases(scope); initReveal(scope); initManifesto(scope); initSheet(scope); initStack(scope); initFinder(scope); };
  boot();
  initDescription();
  initSticky();
  initRecovery();
  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
